import { useCallback, useEffect, useRef, useState, type JSX, type ReactNode } from 'react';

interface Props {
  title: string;
  subtitle?: string;
  x: number;
  y: number;
  z: number;
  width?: number;
  onMove(x: number, y: number): void;
  onFocus(): void;
  onClose(): void;
  children: ReactNode;
}

/** A draggable, focusable popup. Faceplates and tools live in these. */
export function FloatingWindow({
  title,
  subtitle,
  x,
  y,
  z,
  width = 300,
  onMove,
  onFocus,
  onClose,
  children,
}: Props): JSX.Element {
  const drag = useRef<{ dx: number; dy: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if ((e.target as HTMLElement).closest('button,input,select')) return;
      drag.current = { dx: e.clientX - x, dy: e.clientY - y };
      setDragging(true);
      onFocus();
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    },
    [x, y, onFocus],
  );

  useEffect(() => {
    if (!dragging) return;
    const move = (e: PointerEvent): void => {
      if (!drag.current) return;
      onMove(
        Math.max(0, Math.min(window.innerWidth - 80, e.clientX - drag.current.dx)),
        Math.max(28, Math.min(window.innerHeight - 60, e.clientY - drag.current.dy)),
      );
    };
    const up = (): void => {
      drag.current = null;
      setDragging(false);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [dragging, onMove]);

  return (
    <div
      onMouseDown={onFocus}
      style={{
        position: 'fixed',
        left: x,
        top: y,
        width,
        zIndex: z,
        background: 'var(--panel)',
        border: '1px solid var(--line-dim)',
        borderRadius: 5,
        boxShadow: `0 10px 30px var(--shadow)`,
        userSelect: dragging ? 'none' : undefined,
      }}
    >
      <div
        onPointerDown={onPointerDown}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 8px',
          background: 'var(--panel-2)',
          borderBottom: '1px solid var(--line-dim)',
          borderRadius: '4px 4px 0 0',
          cursor: dragging ? 'grabbing' : 'grab',
        }}
      >
        <strong style={{ fontFamily: 'var(--mono)', fontSize: 12.5 }}>{title}</strong>
        {subtitle && (
          <span className="muted" style={{ fontSize: 11, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {subtitle}
          </span>
        )}
        <button
          onClick={onClose}
          aria-label="Close"
          style={{ marginLeft: 'auto', padding: '0 7px', lineHeight: '18px', background: 'transparent', border: 'none' }}
        >
          ✕
        </button>
      </div>
      <div>{children}</div>
    </div>
  );
}
